import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, ButtonGroup, ClickAwayListener, Grow, Link, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DataTable from '../../../../../@Common/DataTable'
import { useDispatch } from 'react-redux'
import { showErrorNotification, showModal } from '../../../../../../redux/actions'
import NoRows from '../../../../../@Common/DataTable/NoRows'
import moment from 'moment'
import IntegrationOptionsMenu from './IntegrationOptionsMenu'
import useIntegrations from './useIntegrations'
import PropTypes from 'prop-types'
import { getNetSuiteCustomerAddress } from '../../../../../../setup/configuration'
import { useOutletContext } from 'react-router-dom'

const IntegrationsSection = ({
}) => {
  const dispatch = useDispatch()

  const { partnerListingId, openManufacturerModal } = useOutletContext()

  const {
    partnerListingIntegrations,
    isLoadingpPartnerListingIntegrations,
    isErrorPartnerListingIntegrations,
    availableAppIntegrations,
    viewListingIntegration,
    openManageConnectionModal,
    openRemoveConnectionModal,
    integrationTypes,
  } = useIntegrations(partnerListingId)

  const hasUnlinkedAvailableApps = () => {
    if (!availableAppIntegrations || !partnerListingIntegrations) return false

    const linkedTypes = new Set(partnerListingIntegrations.map(integration => integration.type))
    return availableAppIntegrations.some(app => !linkedTypes.has(app.type))
  }

  const unlinkedAvailableApps = () => {
    const linkedTypes = partnerListingIntegrations.map(i => i.type)
    return availableAppIntegrations.filter(app => !linkedTypes.includes(app.type))
  }

  const openIntegrationModalByType = (app, isRevokeMode) => {
    switch (app.type) {
      case integrationTypes.GovXShop:
        openManufacturerModal(partnerListingId, app, isRevokeMode)
        break;
      case integrationTypes.NetSuite:
        viewListingIntegration()
        break;
    }
  }

  const handleManageConnection = (row) => {
    const matchingApp = availableAppIntegrations?.find(app => app.type === row.type)
    openManageConnectionModal(row, matchingApp)
  }

  const integrationAppName = (params) => {
    const app = availableAppIntegrations?.find(app => app.type === params.row.type)
    let name = app ? app.name : ''

    switch (params.row.type) {
      case integrationTypes.NetSuite:
        return params.row.netSuiteId ? (
          <>
            {name} (ID:
            <Link
              sx={{ marginLeft: '3px' }}
              href={`${getNetSuiteCustomerAddress() + params.row.netSuiteId}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {params.row.netSuiteId}
            </Link>
            )
          </>
        ) : (
          name
        )
      default:
        return name
    }
  }

  const columns = [
    {
      field: 'application', headerName: 'Application', filterable: false, flex: 1,
      renderCell: (params) => integrationAppName(params)
    },
    {
      field: 'dateLinkedUtc', headerName: 'Date Linked', filterable: false, flex: 2,
      renderCell: (params) => params.row.dateLinkedUtc && !params.row.isProcessing ? `${moment(params.row.dateLinkedUtc).format('MM/DD/YYYY')}` : 'Processing',
    },
    {
      field: '',
      headerName: '',
      width: 20,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const row = { ...params.row, name: params.row.applicationName }
        return (
          <IntegrationOptionsMenu
            row={row}
            onClickRevokeAccess={() => openIntegrationModalByType(row, true)}
            onClickManageConnection={() => handleManageConnection(row)}
            onClickRemoveConnection={() => openRemoveConnectionModal(row)}
          />
        )
      }
    },
  ]

  useEffect(() => {
    if (isErrorPartnerListingIntegrations) {
      dispatch(showErrorNotification('There was an error while attempting to load GovX Shop integrations. Please try again later.'))
    }
  }, [isErrorPartnerListingIntegrations, dispatch])

  const [connectMenuOpen, setConnectMenuOpen] = useState(false)
  const anchorRef = useRef(null)

  const handleMenuItemClick = (app) => {
    setConnectMenuOpen(false)
    openIntegrationModalByType(app, false)
  }

  return (
    <Box paddingLeft={4}>
      {/* Connect Dropdown */}
      {hasUnlinkedAvailableApps() && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <ButtonGroup variant="contained" ref={anchorRef}>
            <Button onClick={() => setConnectMenuOpen((prev) => !prev)}>
              CONNECT
            </Button>
            <Button
              size="small"
              onClick={() => setConnectMenuOpen((prev) => !prev)}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            open={connectMenuOpen}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            transition
            style={{ zIndex: 1300 }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Paper elevation={4}>
                  <ClickAwayListener onClickAway={() => setConnectMenuOpen(false)}>
                    <MenuList>
                      {unlinkedAvailableApps().map((app) => (
                        <MenuItem
                          key={app.name}
                          onClick={() => handleMenuItemClick(app)}
                          sx={{ gap: 1.5, py: 1 }}
                        >
                          <Box
                            component="img"
                            src={app.imageUrl}
                            alt={app.name}
                            sx={{ width: 24, height: 24, objectFit: 'contain' }}
                          />
                          <Typography variant="body2">{app.name}</Typography>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      )}

      {/* Connections Grid */}
      <DataTable
        rows={partnerListingIntegrations ? partnerListingIntegrations.map(integration => {
          const matchingApp = availableAppIntegrations?.find(app => app.type === integration.type)
          return {
            ...integration,
            id: integration.type,
            applicationName: matchingApp?.name || 'Unknown'
          }
        }) : []}
        isLoading={isLoadingpPartnerListingIntegrations}
        rowSelection={false}
        columns={columns}
        disableColumnMenu
        autoHeight
        minHeight={'160px'}
        hideFooter
        hideToolbar={true}
        noRowsOverlay={isErrorPartnerListingIntegrations ? (
          <NoRows
            message="Failed to load integrations."
          />
        ) : (
          <NoRows
            message="There are no connections for this listing."
          />
        )}
      />
    </Box>
  )
}

IntegrationsSection.propTypes = {
  partnerListingId: PropTypes.string,
  openManufacturerModal: PropTypes.func,
  viewListingIntegration: PropTypes.func,
}

export default IntegrationsSection
